import type { WorkspacePublicId } from '@hookmyapp/shared';
import { UserBlockingError, ValidationError } from '@hookmyapp/observability/errors';
import { isValidPublicId } from '@/lib/public-id';
import { recordPendingState } from '@/lib/meta-popup-context';

const META_APP_ID = import.meta.env.VITE_META_APP_ID;

/**
 * Load the Facebook JS SDK asynchronously (idempotent).
 * Resolves when FB.init() has completed.
 */
export function initFacebookSdk(): Promise<void> {
  return new Promise<void>((resolve) => {
    if (window.FB) {
      resolve();
      return;
    }

    window.fbAsyncInit = () => {
      window.FB!.init({
        appId: META_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v21.0',
      });
      resolve();
    };

    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  });
}

interface OAuthStartResponse {
  state: string;
  redirectUrl: string;
}

/**
 * Launch Meta's WhatsApp embedded signup. Calls /meta/oauth/start to mint a
 * server-side state row (see spec 2026-05-06-meta-oauth-server-side-state-design),
 * then opens the popup at the returned URL. State no longer lives in any
 * browser storage - the nonce is bound to a row keyed on (user_id,
 * workspace_id) and validated at consume time.
 *
 * Popup-blocker handling: window.open MUST be the synchronous gated action,
 * so we open the popup immediately to a placeholder URL (about:blank), then
 * navigate it to the real OAuth URL once the mint resolves. popup.location
 * after an await navigates an already-open browsing context, which does NOT
 * require fresh user activation (per MDN transient-activation).
 */
export async function launchMetaSignup(
  workspaceTarget: WorkspacePublicId | (() => Promise<WorkspacePublicId>),
  fetchWithAuth: (path: string, options?: RequestInit) => Promise<Response>,
  reconnect?: { channelId: string },
): Promise<void> {
  if (typeof workspaceTarget !== 'function' && !isValidPublicId(workspaceTarget, 'ws')) {
    throw new ValidationError({
      message: `launchMetaSignup: invalid wsPublicId "${workspaceTarget}"`,
      code: 'META_SIGNUP_INVALID_WORKSPACE_ID',
      details: { wsPublicId: workspaceTarget },
    });
  }

  const isMobile = matchMedia('(max-width: 768px)').matches;
  const popup = isMobile
    ? null
    : window.open('about:blank', 'wa_embedded_signup', popupFeatures());

  let mint: OAuthStartResponse;
  try {
    const wsPublicId =
      typeof workspaceTarget === 'function' ? await workspaceTarget() : workspaceTarget;
    if (!isValidPublicId(wsPublicId, 'ws')) {
      throw new ValidationError({
        message: `launchMetaSignup: invalid wsPublicId "${wsPublicId}"`,
        code: 'META_SIGNUP_INVALID_WORKSPACE_ID',
        details: { wsPublicId },
      });
    }
    const res = await fetchWithAuth(`/meta/oauth/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Workspace-Id': wsPublicId,
      },
      body: JSON.stringify({
        redirectPath: '/meta/callback/whatsapp',
        // Reconnect-in-place (D4): the channel + mode are authorized + minted
        // server-side; finalize reads them only from the state row.
        ...(reconnect
          ? { channelId: reconnect.channelId, reconnectMode: 'create_new_allowed' }
          : {}),
      }),
    });
    if (!res.ok) {
      throw new UserBlockingError({
        message: `/meta/oauth/start failed: ${res.status}`,
        code: 'META_OAUTH_START_FAIL',
        details: { status: res.status },
      });
    }
    mint = (await res.json()) as OAuthStartResponse;
    // Mark this tab as the flow's owner so the localStorage relay in
    // useMetaSignupListeners only fires here, not in every open tab.
    recordPendingState(mint.state);
  } catch (e) {
    // Mint failed - close any opened popup and re-throw so the caller can
    // toast. Caller must supply an auth-injecting fetch (typically
    // `useFetch()`) - bare `fetch` lacks the WorkOS Bearer token. The caller
    // is also responsible for catching thrown errors and surfacing a toast.
    popup?.close();
    throw e;
  }

  if (isMobile) {
    window.location.assign(mint.redirectUrl);
    return;
  }

  if (popup) {
    popup.location.href = mint.redirectUrl;
    return;
  }

  // Popup blocker - fall through to same-window redirect.
  window.location.assign(mint.redirectUrl);
}

function popupFeatures(): string {
  const w = 720;
  const h = 820;
  const left = Math.round((screen.width - w) / 2);
  const top = Math.round((screen.height - h) / 2);
  return `width=${w},height=${h},left=${left},top=${top},scrollbars=yes`;
}

export type WaEmbeddedSignupPayload = {
  phone_number_id?: string;
  waba_id?: string;
  business_id?: string;
};

/**
 * Listen for WA_EMBEDDED_SIGNUP session events from the Meta popup.
 * Returns a cleanup function to remove the listener.
 */
export function addEmbeddedSignupListener(
  onPayload: (payload: WaEmbeddedSignupPayload) => void,
): () => void {
  const handler = (event: MessageEvent) => {
    if (
      event.origin !== 'https://www.facebook.com' &&
      event.origin !== 'https://web.facebook.com'
    )
      return;
    if (event.data?.type !== 'WA_EMBEDDED_SIGNUP') return;
    const payload = (event.data as { data?: WaEmbeddedSignupPayload }).data;
    if (payload) onPayload(payload);
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
